/*
 * Copyright (c) 2023 Bolmate B.V.
 */
import './style/productSelect.scss';

import React, {KeyboardEventHandler, MouseEventHandler, useContext, useMemo} from 'react';
import {
    AwesomeIcon, ChangeFunction, Checkbox, CloseButton, ElementPosition, getSetting, Loader, remToPx, voidFunction,
    WithBackground
} from '@dvrd/dvr-controls';
import BolProduct from "../../../models/bolProductModel";
import {BolmateButton, BolmateInput} from "../index";
import InfoIcon from "../../icon/infoIcon";
import {AppContext} from "../../misc/appContext";
import InfiniteList, {RenderRowProps} from "../../misc/infiniteList";
import {Icon} from '@iconify/react';
import classNames from 'classnames';

interface Props {
    onClose: MouseEventHandler;
    onSelectAll: MouseEventHandler;
    onSelectRemaining: ChangeFunction<boolean>;
    onSelectProduct: (product: BolProduct) => MouseEventHandler;
    onLoadMore: (startIndex: number) => Promise<void>;
    onChangeSearch: ChangeFunction<string>;
    onImportProducts: MouseEventHandler;
    onSubmit: MouseEventHandler;
    onEnterSearch: KeyboardEventHandler;
    products: Array<BolProduct>;
    except: Array<BolProduct>;
    active: boolean;
    loading: boolean;
    search: string;
    totalProducts: number;
    importing: boolean;
    allSelected: boolean;
    remainingSelected: boolean;
    loadingMore: boolean;
}

const ITEM_SIZE = remToPx(2.5);

function getRemainingProducts(products: Array<BolProduct>,
                              exceptProducts: Array<BolProduct> | undefined): Array<BolProduct> {
    if (!exceptProducts) return products;
    const remaining: Array<BolProduct> = [];
    for (const product of products) {
        if (!exceptProducts.find((prod: BolProduct) => prod.ean === product.ean))
            remaining.push(product);
    }
    return remaining;
}

export default function ProductSelect(props: Props) {
    const context = useContext(AppContext);
    const {
        products, except, allSelected, onSelectProduct, onSelectAll, onSelectRemaining, importing, loading, onLoadMore,
        onClose, search, totalProducts, active, remainingSelected, loadingMore, onChangeSearch, onImportProducts,
        onSubmit, onEnterSearch
    } = props;
    const hasMore = useMemo(() => {
        return products.length < totalProducts;
    }, [products, totalProducts]);
    const btnLabel = useMemo(() => {
        return !!search.length ? 'Volgende' : 'Klaar';
    }, [search]);

    function renderSearchBar() {
        const hideSyncForUsers: Array<string> = getSetting('hideProductSyncForUsers') ?? [];
        const {user} = context.userContext;
        const hideSyncButton = !!user && hideSyncForUsers.includes(user?.id);
        return (
            <div className='search-bar'>
                <BolmateInput onChange={onChangeSearch} value={search} label='Zoek een product'
                              disabled={loading || importing} inputProps={{placeholder: 'EAN of productnaam'}}
                              ornaments={{element: <AwesomeIcon name='search'/>}} onEnter={onEnterSearch}/>
                {!hideSyncButton &&
                    <BolmateButton onClick={onImportProducts} label='Sync met Bol' disabled={loading || importing}
                                   ornaments={{element: <AwesomeIcon name='rotate'/>}} secondary/>}
            </div>
        )
    }

    function renderGlobalActions() {
        return (
            <div className='global-actions'>
                <div className='with-info'>
                    <Checkbox onCheck={onSelectRemaining} checked={remainingSelected}
                              label='Resterende producten selecteren' disabled={importing}/>
                    <div className='info-container'>
                        <InfoIcon/>
                        <p className='info-text'>Met deze optie worden automatisch alle producten geselecteerd
                            waarvoor nog geen andere mail ingesteld is.</p>
                    </div>
                </div>
            </div>
        )
    }

    function renderAllButton() {
        const icon = <Icon icon='mdi:arrow' className='arrow-icon'/>;
        const label = allSelected ? 'Alles deselecteren' : 'Alles selecteren';
        const placement = allSelected ? ElementPosition.LEFT : ElementPosition.RIGHT;
        return (
            <BolmateButton onClick={onSelectAll} label={label} ornaments={{element: icon, placement}} disabled={loading}
                           className={classNames('btn-all', allSelected && 'selected')}/>
        )
    }

    function renderImportStatus() {
        if (!importing) return null;
        let status: string = 'Bezig met het importeren van je producten...';
        return (
            <Loader active label={status}/>
        )
    }

    function renderDisabledOverlay() {
        if (!remainingSelected) return null;
        return (
            <div className='overlay'>
                <div className='disabled-text'>
                    <label className='disabled-title'>Resterende producten geselecteerd</label>
                    <label>Linksboven het vakje "Resterende producten selecteren" om resterende/geen producten te
                        selecteren</label>
                </div>
            </div>
        )
    }

    function renderAvailableList() {
        const _products = allSelected ? except : getRemainingProducts(products, except);
        const _hasMore = allSelected ? false : hasMore;
        return (
            <div>
                <InfiniteList onLoadMore={onLoadMore} renderRow={renderListRow(false)} hasMore={_hasMore}
                              isLoadingMore={loadingMore} itemCount={_products.length} itemSize={ITEM_SIZE}
                              stickyIndexes={[0]} renderStickyRow={renderListHead}/>
            </div>
        );
    }

    function renderSelectedList() {
        const _products = allSelected ? getRemainingProducts(products, except) : except;
        const _hasMore = allSelected ? hasMore : false;
        return (
            <div>
                <InfiniteList onLoadMore={onLoadMore} renderRow={renderListRow(true)} hasMore={_hasMore}
                              isLoadingMore={loadingMore} itemCount={_products.length} itemSize={ITEM_SIZE}
                              stickyIndexes={[0]} renderStickyRow={renderListHead}/>
            </div>
        );
    }

    function renderLists() {
        let selectedLabel: string | number;
        let availableLabel: string | number;
        if (remainingSelected) {
            availableLabel = totalProducts;
            selectedLabel = 'resterende producten';
        } else if (allSelected) {
            availableLabel = except.length;
            selectedLabel = totalProducts - except.length;
        } else {
            availableLabel = totalProducts - except.length;
            selectedLabel = except.length;
        }
        return (
            <div className='product-lists'>
                <label className='list-title'>Beschikbare producten{!!search.length && ` met zoekterm '${search}' `}
                    ({availableLabel})</label>
                <div className='divider'/>
                <label className='list-title'>Geselecteerde
                    producten{!!search.length && ` met zoekterm '${search}' `} ({selectedLabel})</label>
                {renderAvailableList()}
                {renderSelectedList()}
            </div>
        )
    }

    function renderListHead(props: RenderRowProps) {
        return (
            <div key={props.index} style={props.style} className='default-table-row list-row head'>
                <div/>
                <label>EAN</label>
                <label>Productnaam</label>
            </div>
        )
    }

    function renderListRow(isSelected: boolean) {
        return function(props: RenderRowProps) {
            const {style, index, isLoaded} = props;
            if (!isLoaded) return (
                <div style={style} className='default-table-row list-row'>
                    <div/>
                    <label>Product laden...</label>
                    <label/>
                </div>
            );
            let useList: Array<BolProduct>;
            if (allSelected) {
                if (isSelected) useList = getRemainingProducts(products, except);
                else useList = except;
            } else {
                if (isSelected) useList = except;
                else useList = getRemainingProducts(products, except);
            }
            const product = useList[index];
            if (!product) return <div/>;
            return (
                <div key={index} style={style} className='default-table-row list-row'
                     onClick={onSelectProduct(product)}>
                    <Checkbox onCheck={voidFunction} checked={isSelected}/>
                    <label>{product.ean}</label>
                    <label>{product.title}</label>
                </div>
            )
        }
    }

    return (
        <WithBackground active={active} onClose={onClose}>
            <div className='product-select default-popup'>
                <div className='header-container'>
                    <label className='header-title'>Producten selecteren</label>
                    <CloseButton onClick={onClose}/>
                </div>
                <div className='body-container'>
                    <Loader active={loading && !importing}/>
                    {renderImportStatus()}
                    {renderSearchBar()}
                    {renderGlobalActions()}
                    <div className='lists-container'>
                        {renderDisabledOverlay()}
                        {renderAllButton()}
                        {renderLists()}
                    </div>
                </div>
                <div className='footer-container'>
                    <label className={classNames('search-label', !search.length && 'hidden')}>Zoekterm
                        '{search}'</label>
                    <BolmateButton onClick={onSubmit} label={btnLabel} disabled={loading} secondary/>
                </div>
            </div>
        </WithBackground>
    )
}